import { useState, type FormEvent, type ReactNode } from 'react';
import { Save } from 'lucide-react';
import { DNS_RECORD_TYPES, PRIORITY_TYPES, PROXIABLE_TYPES, type DnsRecordType } from '@cp/shared';
import { Button, Checkbox, Dialog, Field, Input, Select } from '@/components/ui';
import { useRecordChange } from '@/hooks/useDomains';
import { toast } from '@/store/toastStore';
import { errorField, errorText } from '@/utils/errors';
import type { DnsRecordView, SiteZoneView } from '@/types/api';

const TYPE_OPTIONS = DNS_RECORD_TYPES.map((type) => ({ value: type, label: type }));

const TTL_OPTIONS = [
  { value: '1', label: 'Авто' },
  { value: '300', label: '5 минут' },
  { value: '3600', label: '1 час' },
  { value: '86400', label: '1 день' },
];

const CONTENT_HINTS: Partial<Record<DnsRecordType, string>> = {
  A: 'IPv4-адрес сервера, например 45.140.14.6',
  AAAA: 'IPv6-адрес сервера',
  CNAME: 'Имя, на которое ссылается запись',
  TXT: 'Текст: подтверждение Search Console, SPF, DKIM…',
  MX: 'Почтовый сервер',
};

/** The name as people write it in a zone: `@` for the domain itself, `www` for www under it. */
function shortName(name: string, apex: string): string {
  if (name === apex) return '@';
  return name.endsWith(`.${apex}`) ? name.slice(0, -(apex.length + 1)) : name;
}

/** Adding or changing one DNS record; saved in 2d2s the moment it is confirmed. */
export function DnsRecordDialog({
  siteId,
  zone,
  record,
  onClose,
}: {
  readonly siteId: string;
  readonly zone: SiteZoneView;
  /** Null to add a new one. */
  readonly record: DnsRecordView | null;
  readonly onClose: () => void;
}): ReactNode {
  const [type, setType] = useState<DnsRecordType>((record?.type as DnsRecordType | undefined) ?? 'A');
  const [name, setName] = useState(record ? shortName(record.name, zone.apex) : '@');
  const [content, setContent] = useState(record?.content ?? '');
  const [ttl, setTtl] = useState(String(record?.ttl ?? 1));
  const [proxied, setProxied] = useState(record?.proxied ?? true);
  const [priority, setPriority] = useState(record?.priority === null || record?.priority === undefined ? '10' : String(record.priority));

  const change = useRecordChange(siteId, {
    onSuccess: () => {
      toast.success(record ? 'Запись изменена' : 'Запись добавлена');
      onClose();
    },
  });
  const field = change.isError ? errorField(change.error) : null;
  const proxiable = PROXIABLE_TYPES.includes(type) && zone.cloudflare;
  const withPriority = PRIORITY_TYPES.includes(type);

  function submit(event: FormEvent): void {
    event.preventDefault();
    const body = {
      type,
      name: name.trim(),
      content: content.trim(),
      ttl: Number(ttl),
      ...(proxiable ? { proxied } : {}),
      ...(withPriority ? { priority: Number(priority) } : {}),
    };
    change.mutate(record ? { kind: 'update', domainId: zone.domainId!, recordId: record.id, record: body } : { kind: 'create', domainId: zone.domainId!, record: body });
  }

  const error = (which: string, text: string) => (field === which ? { error: change.isError ? errorText(change.error) : text } : {});

  return (
    <Dialog
      open
      title={record ? 'Изменить запись' : 'Новая запись'}
      description={zone.apex}
      onClose={() => (change.isPending ? undefined : onClose())}
      size="sm"
      footer={
        <>
          <Button onClick={onClose} disabled={change.isPending}>
            Отмена
          </Button>
          <Button variant="primary" icon={Save} busy={change.isPending} type="submit" form="dns-record" disabled={!name.trim() || !content.trim()}>
            Сохранить
          </Button>
        </>
      }
    >
      <form id="dns-record" className="stack" onSubmit={submit} noValidate>
        <div className="row-tight">
          <Field label="Тип" htmlFor="dns-type">
            <Select id="dns-type" label="Тип" value={type} onChange={setType} options={TYPE_OPTIONS} />
          </Field>
          <Field label="TTL" htmlFor="dns-ttl">
            <Select id="dns-ttl" label="TTL" value={ttl} onChange={setTtl} options={TTL_OPTIONS} />
          </Field>
        </div>
        <Field label="Имя" htmlFor="dns-name" hint={`@ — сам ${zone.apex}; www — www.${zone.apex}`} {...error('name', 'Недопустимое имя')}>
          <Input id="dns-name" value={name} onChange={(event) => setName(event.target.value)} invalid={field === 'name'} autoComplete="off" />
        </Field>
        <Field label="Значение" htmlFor="dns-content" {...(CONTENT_HINTS[type] ? { hint: CONTENT_HINTS[type] } : {})} {...error('content', 'Недопустимое значение')}>
          <Input id="dns-content" value={content} onChange={(event) => setContent(event.target.value)} invalid={field === 'content'} autoComplete="off" />
        </Field>
        {withPriority ? (
          <Field label="Приоритет" htmlFor="dns-priority" {...error('priority', 'Число от 0 до 65535')}>
            <Input id="dns-priority" value={priority} onChange={(event) => setPriority(event.target.value)} inputMode="numeric" invalid={field === 'priority'} />
          </Field>
        ) : null}
        {proxiable ? (
          <Checkbox
            checked={proxied}
            onChange={setProxied}
            label="Через прокси Cloudflare"
            hint="Скрывает адрес сервера, включает кэш и защиту. Не используется для почтовых записей"
          />
        ) : null}
        {change.isError && !field ? (
          <p className="field-error" role="alert">
            {errorText(change.error)}
          </p>
        ) : null}
      </form>
    </Dialog>
  );
}
